import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getQANotes, getQANotesStats, getUsers, markQANoteRead } from '@/lib/api';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Search,
  Filter,
  User,
  Phone,
  Calendar,
  Clock,
  Eye,
  CheckCircle,
  AlertCircle,
  ThumbsUp,
  TrendingUp,
  MessageSquare,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Shield
} from 'lucide-react';
import { format } from 'date-fns';

const NOTE_TYPE_CONFIG = {
  general: { label: 'General', color: 'bg-muted text-slate-700', icon: MessageSquare },
  feedback: { label: 'Feedback', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
  action_required: { label: 'Action Required', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  positive: { label: 'Positive', color: 'bg-green-100 text-green-700', icon: ThumbsUp },
  improvement: { label: 'Needs Improvement', color: 'bg-amber-100 text-amber-700', icon: TrendingUp }
};

export default function QANotes() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filterUser, setFilterUser] = useState(searchParams.get('user_id') || 'all');
  const [filterAuditor, setFilterAuditor] = useState(searchParams.get('auditor_id') || 'all');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Selected note dialog
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
    fetchStats();
    fetchUsersAndAuditors();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterUser, filterAuditor, filterType, searchQuery, page]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = {
        limit,
        skip: page * limit
      };
      
      if (filterUser !== 'all') params.user_id = filterUser;
      if (filterAuditor !== 'all') params.auditor_id = filterAuditor;
      if (filterType !== 'all') params.note_type = filterType;
      if (searchQuery) params.search = searchQuery;
      
      const { data } = await getQANotes(params);
      setNotes(data.notes || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch QA notes:', error);
      toast.error('Failed to load QA notes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const { data } = await getQANotesStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchUsersAndAuditors = async () => {
    try {
      // Get all users
      const { data: usersData } = await getUsers();
      setUsers(usersData || []);
      
      // Get tenant users (auditors)
      const { data: tenantUsersData } = await api.get('/tenant/users');
      const allTenantUsers = tenantUsersData.users || tenantUsersData || [];
      // Filter for admins and quality auditors
      const auditorsList = allTenantUsers.filter(u => 
        u.is_admin || u.role === 'admin' || u.role === 'quality_auditor' || u.role_id === 'quality_auditor'
      );
      setAuditors(auditorsList);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleViewNote = async (note) => {
    setSelectedNote(note);
    setNoteDialogOpen(true);
    
    // Mark as read if user is the target
    if (user?.id === note.user_id && !note.is_read_by_user) {
      try {
        await markQANoteRead(note.id);
        // Update local state
        setNotes(prev => prev.map(n => 
          n.id === note.id ? { ...n, is_read_by_user: true } : n
        ));
      } catch (error) {
        console.error('Failed to mark note as read:', error);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="qa-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">QA Notes</h1>
          <p className="text-muted-foreground">Review and manage all quality assurance notes</p>
        </div>
        <Button onClick={() => { fetchData(); fetchStats(); }} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Notes</p>
                <p className="text-2xl font-bold text-white">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : stats?.total_notes || 0}
                </p>
              </div>
              <FileText className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unread Notes</p>
                <p className="text-2xl font-bold text-amber-500">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : stats?.unread_count || 0}
                </p>
              </div>
              <Eye className="h-8 w-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold text-green-500">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : stats?.recent_activity || 0}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Auditors</p>
                <p className="text-2xl font-bold text-purple-500">
                  {isLoadingStats ? <Skeleton className="h-8 w-16" /> : stats?.notes_by_auditor?.length || 0}
                </p>
              </div>
              <Shield className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-muted-foreground mb-1 block">Search Notes</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by note content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="qa-notes-search"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm text-muted-foreground mb-1 block">Filter by User</label>
              <Select value={filterUser} onValueChange={(v) => { setFilterUser(v); setPage(0); }}>
                <SelectTrigger data-testid="filter-user">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm text-muted-foreground mb-1 block">Filter by Auditor</label>
              <Select value={filterAuditor} onValueChange={(v) => { setFilterAuditor(v); setPage(0); }}>
                <SelectTrigger data-testid="filter-auditor">
                  <SelectValue placeholder="All Auditors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Auditors</SelectItem>
                  {auditors.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <label className="text-sm text-muted-foreground mb-1 block">Note Type</label>
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(0); }}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(NOTE_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Notes Table */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">QA Notes ({total})</CardTitle>
            <div className="text-sm text-muted-foreground">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No QA notes found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Note</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Call</TableHead>
                  <TableHead>Auditor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notes.map(note => {
                  const typeConfig = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.general;
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <TableRow 
                      key={note.id} 
                      className={`cursor-pointer hover:bg-slate-800/50 ${!note.is_read_by_user && user?.id === note.user_id ? 'bg-blue-900/20' : ''}`}
                      onClick={() => handleViewNote(note)}
                      data-testid={`qa-note-row-${note.id}`}
                    >
                      <TableCell className="max-w-[300px]">
                        <p className="truncate text-white font-medium">
                          {note.note_text.substring(0, 80)}{note.note_text.length > 80 ? '...' : ''}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span 
                            className="text-blue-400 hover:underline cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/users?highlight=${note.user_id}`);
                            }}
                          >
                            {note.user_info?.name || 'Unknown User'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div 
                          className="flex items-center gap-2 text-blue-400 hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/call/${note.call_id}`);
                          }}
                        >
                          <Phone className="h-4 w-4" />
                          <span>View Call</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-purple-400" />
                          <span className="text-muted-foreground/70">{note.auditor_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeConfig.color}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(note.created_at)}
                      </TableCell>
                      <TableCell>
                        {note.is_read_by_user ? (
                          <Badge variant="outline" className="text-green-500 border-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Read
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-500 border-amber-500">
                            <Clock className="h-3 w-3 mr-1" />
                            Unread
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewNote(note);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note Detail Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              QA Note Details
            </DialogTitle>
            <DialogDescription>
              Review the full note and related information
            </DialogDescription>
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-4">
              {/* Note Type Badge */}
              <div className="flex items-center gap-2">
                <Badge className={NOTE_TYPE_CONFIG[selectedNote.note_type]?.color || 'bg-muted'}>
                  {NOTE_TYPE_CONFIG[selectedNote.note_type]?.label || 'General'}
                </Badge>
                {selectedNote.is_read_by_user ? (
                  <Badge variant="outline" className="text-green-500">Read</Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-500">Unread</Badge>
                )}
              </div>
              
              {/* Note Content */}
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-white whitespace-pre-wrap">{selectedNote.note_text}</p>
              </div>
              
              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>User:</span>
                    <span className="text-white">{selectedNote.user_info?.name || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shield className="h-4 w-4" />
                    <span>Auditor:</span>
                    <span className="text-white">{selectedNote.auditor_name}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>Created:</span>
                    <span className="text-white">{formatDate(selectedNote.created_at)}</span>
                  </div>
                  {selectedNote.read_at && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Eye className="h-4 w-4" />
                      <span>Read at:</span>
                      <span className="text-white">{formatDate(selectedNote.read_at)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Call Info */}
              {selectedNote.call_info && (
                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Related Call</h4>
                  <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                    <div>
                      <p className="text-white">{selectedNote.call_info.agent_name || 'Call'}</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedNote.call_info.call_datetime ? formatDate(selectedNote.call_info.call_datetime) : 'No date'}
                      </p>
                    </div>
                    {selectedNote.call_info.quality_score && (
                      <Badge className="bg-blue-100 text-blue-700">
                        Score: {selectedNote.call_info.quality_score}
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNoteDialogOpen(false);
                        navigate(`/dashboard/call/${selectedNote.call_id}`);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Call
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
